import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Check, X, Download, Upload, Search, RefreshCw, Database } from "lucide-react";
import { PORTFOLIO_SEED } from "@/data/portfolioIntelligence";
import { API } from "@/App";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ARRAY_FIELDS = ["val_trend", "rev_trend", "hc_trend", "traffic_trend"];
const SENTIMENT_OPTIONS = ["", "POSITIVE", "NEGATIVE", "NEUTRAL", "CAUTIONARY", "MIXED"];

const arrToStr = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");
const strToArr = (str) =>
  (str || "").split(",").map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !isNaN(n));

function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const INPUT_BASE = {
  fontSize: "11px",
  border: "1px solid #DCDFE2",
  borderRadius: "4px",
  padding: "3px 6px",
  outline: "none",
  background: "#FFFFFF",
  color: "#0A0A0A",
  width: "100%",
  boxSizing: "border-box",
};

const CELL_SEED = { fontSize: "11px", color: "#9AA0A6", fontStyle: "italic" };
const CELL_DB   = { fontSize: "11px", color: "#0A0A0A" };

// ─── EditableCell ─────────────────────────────────────────────────────────────
function EditableCell({ field, value, isEditing, editValues, onEditChange, isDbSaved }) {
  const displayVal = ARRAY_FIELDS.includes(field) ? arrToStr(value) : (value ?? "");
  const cellStyle = isDbSaved ? CELL_DB : CELL_SEED;

  if (isEditing) {
    if (field === "sentiment") {
      return (
        <select
          value={editValues[field] ?? ""}
          onChange={(e) => onEditChange(field, e.target.value)}
          style={{ ...INPUT_BASE, width: "100px" }}
          data-testid={`edit-${field}`}
        >
          {SENTIMENT_OPTIONS.map((o) => (
            <option key={o} value={o}>{o || "—"}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field === "valuation_b" || field === "glassdoor_rating" ? "number" : "text"}
        value={editValues[field] ?? ""}
        onChange={(e) => onEditChange(field, e.target.value)}
        style={INPUT_BASE}
        data-testid={`edit-${field}`}
        placeholder={ARRAY_FIELDS.includes(field) ? "e.g. 1, 5, 12" : ""}
        step={field === "glassdoor_rating" ? "0.1" : undefined}
        min={field === "glassdoor_rating" ? "0" : undefined}
        max={field === "glassdoor_rating" ? "5" : undefined}
      />
    );
  }

  if (!displayVal && displayVal !== 0) {
    return <span style={{ color: "#D1D5DB", fontSize: "11px" }}>—</span>;
  }

  return (
    <span style={cellStyle} title={!isDbSaved ? "Seed data — click Edit to override" : undefined}>
      {String(displayVal)}
    </span>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PortfolioDataSettings() {
  const [dbData, setDbData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDbData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/portfolio-intel`);
      if (res.ok) {
        const data = await res.json();
        setDbData(data);
      } else {
        toast.error("Failed to load database overrides");
      }
    } catch {
      toast.error("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDbData(); }, []);

  // Build merged rows
  const allRows = useMemo(() =>
    Object.entries(PORTFOLIO_SEED).map(([permalink, seed]) => ({
      permalink,
      seed,
      dbOverride: dbData[permalink] || null,
      merged: { ...seed, ...(dbData[permalink] || {}) },
    })),
    [dbData]
  );

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) => r.seed.name.toLowerCase().includes(q) || r.permalink.toLowerCase().includes(q)
    );
  }, [allRows, searchText]);

  const dbSavedCount = useMemo(
    () => Object.keys(dbData).filter((k) => k in PORTFOLIO_SEED).length,
    [dbData]
  );

  // ── Edit handlers ──────────────────────────────────────────────────────────
  const startEdit = (row) => {
    setEditingRow(row.permalink);
    const m = row.merged;
    setEditValues({
      valuation_b:       m.valuation_b ?? "",
      val_trend:         arrToStr(m.val_trend),
      rev_trend:         arrToStr(m.rev_trend),
      hc_trend:          arrToStr(m.hc_trend),
      traffic_trend:     arrToStr(m.traffic_trend),
      linkedin_followers: m.linkedin_followers ?? "",
      glassdoor_rating:  m.glassdoor_rating ?? "",
      sentiment:         m.sentiment ?? "",
      talent_signal:     m.talent_signal ?? "",
    });
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditValues({});
  };

  const onEditChange = (field, val) => {
    setEditValues((prev) => ({ ...prev, [field]: val }));
  };

  const saveRow = async (permalink) => {
    setSavingRow(permalink);
    try {
      const payload = {};
      if (editValues.valuation_b !== "") payload.valuation_b = parseFloat(editValues.valuation_b);
      if (editValues.val_trend)     payload.val_trend     = strToArr(editValues.val_trend);
      if (editValues.rev_trend)     payload.rev_trend     = strToArr(editValues.rev_trend);
      if (editValues.hc_trend)      payload.hc_trend      = strToArr(editValues.hc_trend);
      if (editValues.traffic_trend) payload.traffic_trend = strToArr(editValues.traffic_trend);
      if (editValues.linkedin_followers) payload.linkedin_followers = editValues.linkedin_followers;
      if (editValues.glassdoor_rating !== "") payload.glassdoor_rating = parseFloat(editValues.glassdoor_rating);
      if (editValues.sentiment)     payload.sentiment     = editValues.sentiment;
      if (editValues.talent_signal) payload.talent_signal = editValues.talent_signal;

      const res = await fetch(`${API}/portfolio-intel/${permalink}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const result = await res.json();
        setDbData((prev) => ({ ...prev, [permalink]: result.data }));
        setEditingRow(null);
        toast.success(`Saved ${PORTFOLIO_SEED[permalink]?.name || permalink}`);
      } else {
        toast.error("Failed to save — server error");
      }
    } catch {
      toast.error("Failed to save — network error");
    } finally {
      setSavingRow(null);
    }
  };

  // ── CSV Export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = [
      "permalink","name","valuation_b","val_trend","rev_trend",
      "hc_trend","traffic_trend","linkedin_followers","glassdoor_rating",
      "sentiment","talent_signal"
    ];
    const rows = allRows.map((r) => {
      const m = r.merged;
      const vals = [
        r.permalink,
        r.seed.name,
        m.valuation_b ?? "",
        arrToStr(m.val_trend),
        arrToStr(m.rev_trend),
        arrToStr(m.hc_trend),
        arrToStr(m.traffic_trend),
        m.linkedin_followers ?? "",
        m.glassdoor_rating ?? "",
        m.sentiment ?? "",
        m.talent_signal ?? "",
      ];
      return vals.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `portfolio_data_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${allRows.length} companies to CSV`);
  };

  // ── CSV Import ──────────────────────────────────────────────────────────────
  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length < 2) { toast.error("CSV is empty or has no data rows"); return; }

      const headers = parseCSVLine(lines[0]);
      const items = [];

      for (let i = 1; i < lines.length; i++) {
        const vals = parseCSVLine(lines[i]);
        const row = {};
        headers.forEach((h, j) => { row[h] = vals[j] ?? ""; });
        if (!row.permalink) continue;

        const item = { permalink: row.permalink };
        if (row.valuation_b)       item.valuation_b     = parseFloat(row.valuation_b);
        if (row.val_trend)         item.val_trend        = strToArr(row.val_trend);
        if (row.rev_trend)         item.rev_trend        = strToArr(row.rev_trend);
        if (row.hc_trend)          item.hc_trend         = strToArr(row.hc_trend);
        if (row.traffic_trend)     item.traffic_trend    = strToArr(row.traffic_trend);
        if (row.linkedin_followers) item.linkedin_followers = row.linkedin_followers;
        if (row.glassdoor_rating)  item.glassdoor_rating = parseFloat(row.glassdoor_rating);
        if (row.sentiment)         item.sentiment        = row.sentiment;
        if (row.talent_signal)     item.talent_signal    = row.talent_signal;

        items.push(item);
      }

      if (!items.length) { toast.error("No valid rows found in CSV"); return; }

      const res = await fetch(`${API}/portfolio-intel/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(items),
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Imported ${result.updated} companies`);
        if (result.errors?.length) toast.warning(`${result.errors.length} rows had errors`);
        await fetchDbData();
      } else {
        toast.error("Import failed — server error");
      }
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  const COL_STYLE = { padding: "8px 10px", whiteSpace: "nowrap", fontSize: "10px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", borderBottom: "2px solid #DCDFE2", background: "#F5F5F5" };
  const TD = { padding: "8px 10px", borderBottom: "1px solid #F0F0F0", verticalAlign: "middle" };

  return (
    <div style={{ background: "#FFFFFF", minHeight: "100%" }}>
      <div style={{ padding: "24px 32px" }}>

        {/* Header */}
        <div className="mb-6">
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
            <Database size={20} style={{ color: "#97C8A6" }} />
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "28px", fontWeight: 700, color: "#0A0A0A" }}>
              Portfolio Data Editor
            </h1>
          </div>
          <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "4px" }}>
            View and edit intelligence data for all {Object.keys(PORTFOLIO_SEED).length} portfolio companies.
            {" "}{dbSavedCount > 0
              ? <span style={{ color: "#97C8A6", fontWeight: 600 }}>{dbSavedCount} companies have DB overrides.</span>
              : <span style={{ color: "#9AA0A6" }}>No DB overrides yet — all data shows seed defaults.</span>
            }
          </p>

        </div>

        {/* Action Bar */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
          {/* Search */}
          <div style={{ position: "relative", flex: "1", minWidth: "200px", maxWidth: "320px" }}>
            <Search size={13} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9AA0A6" }} />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              data-testid="portfolio-data-search"
              style={{ width: "100%", paddingLeft: "30px", paddingRight: "10px", paddingTop: "7px", paddingBottom: "7px", fontSize: "12px", border: "1px solid #DCDFE2", borderRadius: "6px", outline: "none", color: "#0A0A0A" }}
            />
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {/* Refresh */}
            <button
              onClick={fetchDbData}
              disabled={loading}
              data-testid="portfolio-data-refresh"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 14px", fontSize: "12px", fontWeight: 500, borderRadius: "6px", border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#4A4A4A", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F5F5F5"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            {/* Export CSV */}
            <button
              onClick={exportCSV}
              data-testid="portfolio-data-export"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 14px", fontSize: "12px", fontWeight: 500, borderRadius: "6px", border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#4A4A4A", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#F5F5F5"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
            >
              <Download size={12} />
              Export CSV
            </button>

            {/* Import CSV */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importLoading}
              data-testid="portfolio-data-import"
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "7px 14px", fontSize: "12px", fontWeight: 600, borderRadius: "6px", border: "none", background: "#181B20", color: "#FFFFFF", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#272B2E"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#181B20"; }}
            >
              <Upload size={12} />
              {importLoading ? "Importing..." : "Import CSV"}
            </button>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={importCSV} style={{ display: "none" }} data-testid="portfolio-data-file-input" />
          </div>
        </div>

        {/* Table */}
        <div style={{ background: "#FFFFFF", borderRadius: "6px", boxShadow: "0 1px 4px rgba(0,0,0,0.10)", overflow: "hidden", border: "1px solid #DCDFE2" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "1400px" }}>
              <thead>
                <tr>
                  {[
                    { label: "Company",    w: "160px" },
                    { label: "Valuation $B", w: "90px" },
                    { label: "Val Trend",  w: "160px" },
                    { label: "Rev Trend $M", w: "160px" },
                    { label: "HC Trend",   w: "140px" },
                    { label: "Traffic Trend", w: "150px" },
                    { label: "LinkedIn",   w: "100px" },
                    { label: "Glassdoor",  w: "80px" },
                    { label: "Sentiment",  w: "110px" },
                    { label: "Talent Signal", w: "110px" },
                    { label: "Source",     w: "70px" },
                    { label: "Actions",    w: "80px" },
                  ].map((c) => (
                    <th key={c.label} style={{ ...COL_STYLE, width: c.w, minWidth: c.w }}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(12)].map((__, j) => (
                        <td key={j} style={{ ...TD }}>
                          <div style={{ height: "14px", background: "#F5F5F5", borderRadius: "4px", animation: "pulse 1.5s ease-in-out infinite" }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={12} style={{ padding: "48px", textAlign: "center", color: "#9AA0A6", fontSize: "13px", fontStyle: "italic" }}>
                      No companies match your search
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row, idx) => {
                    const isEditing  = editingRow === row.permalink;
                    const isSaving   = savingRow  === row.permalink;
                    const isDbSaved  = !!row.dbOverride;
                    const m          = row.merged;
                    const rowBg      = isEditing ? "#F0F9F4" : idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA";

                    return (
                      <tr key={row.permalink} style={{ background: rowBg, transition: "background 120ms" }}
                        onMouseEnter={(e) => { if (!isEditing) e.currentTarget.style.background = "#F8F9FA"; }}
                        onMouseLeave={(e) => { if (!isEditing) e.currentTarget.style.background = rowBg; }}
                        data-testid={`portfolio-row-${row.permalink}`}
                      >
                        {/* Company */}
                        <td style={{ ...TD, minWidth: "160px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <div>
                              <div style={{ fontSize: "12px", fontWeight: 600, color: "#0A0A0A" }}>{row.seed.name}</div>
                              <div style={{ fontSize: "10px", color: "#9AA0A6" }}>{row.permalink}</div>
                            </div>
                          </div>
                        </td>

                        {/* Valuation */}
                        <td style={{ ...TD }}>
                          <EditableCell field="valuation_b" value={m.valuation_b} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Val Trend */}
                        <td style={{ ...TD }}>
                          <EditableCell field="val_trend" value={m.val_trend} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Rev Trend */}
                        <td style={{ ...TD }}>
                          <EditableCell field="rev_trend" value={m.rev_trend} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* HC Trend */}
                        <td style={{ ...TD }}>
                          <EditableCell field="hc_trend" value={m.hc_trend} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Traffic Trend */}
                        <td style={{ ...TD }}>
                          <EditableCell field="traffic_trend" value={m.traffic_trend} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* LinkedIn */}
                        <td style={{ ...TD }}>
                          <EditableCell field="linkedin_followers" value={m.linkedin_followers} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Glassdoor */}
                        <td style={{ ...TD }}>
                          <EditableCell field="glassdoor_rating" value={m.glassdoor_rating} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Sentiment */}
                        <td style={{ ...TD }}>
                          <EditableCell field="sentiment" value={m.sentiment} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Talent Signal */}
                        <td style={{ ...TD }}>
                          <EditableCell field="talent_signal" value={m.talent_signal} isEditing={isEditing} editValues={editValues} onEditChange={onEditChange} isDbSaved={isDbSaved} />
                        </td>

                        {/* Source badge */}
                        <td style={{ ...TD, textAlign: "center" }}>
                          {isDbSaved ? (
                            <span style={{ fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", background: "#DCFCE7", color: "#15803D" }} data-testid={`source-db-${row.permalink}`}>DB</span>
                          ) : (
                            <span style={{ fontSize: "9px", fontWeight: 700, padding: "2px 6px", borderRadius: "4px", background: "#F3F4F6", color: "#9AA0A6" }} title="Using seed data — click Edit to override" data-testid={`source-seed-${row.permalink}`}>Seed</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={{ ...TD, textAlign: "center" }}>
                          {isEditing ? (
                            <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                              <button
                                onClick={() => saveRow(row.permalink)}
                                disabled={isSaving}
                                data-testid={`save-btn-${row.permalink}`}
                                title="Save"
                                style={{ width: "26px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", border: "none", background: "#97C8A6", color: "#181B20", cursor: "pointer" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#7AB896"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "#97C8A6"; }}
                              >
                                {isSaving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
                              </button>
                              <button
                                onClick={cancelEdit}
                                data-testid={`cancel-btn-${row.permalink}`}
                                title="Cancel"
                                style={{ width: "26px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#6B7280", cursor: "pointer" }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#F5F5F5"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; }}
                              >
                                <X size={11} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(row)}
                              data-testid={`edit-btn-${row.permalink}`}
                              title="Edit"
                              style={{ width: "26px", height: "26px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", border: "1px solid #DCDFE2", background: "#FFFFFF", color: "#6B7280", cursor: "pointer", margin: "0 auto" }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#F5F5F5"; e.currentTarget.style.color = "#0A0A0A"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.color = "#6B7280"; }}
                            >
                              <Pencil size={11} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer count */}
          {!loading && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid #F0F0F0", background: "#FAFAFA", fontSize: "11px", color: "#9AA0A6" }}>
              Showing {filteredRows.length} of {allRows.length} companies
              {dbSavedCount > 0 && ` · ${dbSavedCount} with DB overrides`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
